import { inject, injectable, initializer } from "@launchtray/tsyringe-async";
import { ICanvasScaler } from "../../../canvas/ICanvasScaler";
import { SceneManager } from "../../../scene/SceneManager";
import { Viewable } from "../../../scene/viewable/Viewable";
import { IContextResolver } from "../../composition/IContextResolver";
import { GameScene } from "../game/GameScene";
import { IPreloaderController } from "./controllers/IPreloaderController";
import { IPreloaderModel } from "./models/IPreloaderModel";
import { PreloaderResourcePackage } from "./resources/PreloaderResourcePackage";
import { IApplicationModel } from "../common/models/IApplicationModel";
import { FontResourcesPackage } from "../common/resources/FontResourcesPackage";
import { ILogger } from "../../logger/ILogger";
import { PromiseCompletionSource } from "../../../shared/async/PromiseCompletionSource";
import { HomeScreenPreloaderView } from "./view/HomeScreenPreloaderView";
import { GameInfo } from "../../../analytics/GameInfo";

@injectable()
export class PreloaderScene extends Viewable<void> {
    private readonly preloaderResources: PreloaderResourcePackage;
    private readonly preloaderController: IPreloaderController;
    private readonly preloaderModel: IPreloaderModel;
    private readonly canvasScaler: ICanvasScaler;
    private readonly sceneManager: SceneManager;
    private readonly contextResolver: IContextResolver;
    private readonly applicationModel: IApplicationModel;
    private readonly gameInfo: GameInfo;

    private readonly logger: ILogger;
    private gameScene: GameScene;

    public constructor(
        preloaderResources: PreloaderResourcePackage,
        sceneManager: SceneManager,
        @inject("IPreloaderController") preloaderController: IPreloaderController,
        @inject("IPreloaderModel") preloaderModel: IPreloaderModel,
        @inject("ICanvasScaler") canvasScaler: ICanvasScaler,
        @inject("IContextResolver") contextResolver: IContextResolver,
        @inject("IApplicationModel") applicationModel: IApplicationModel,
        gameInfo: GameInfo,
        @inject("ILogger") logger: ILogger
    ) {
        super();
        this.preloaderResources = preloaderResources;
        this.preloaderController = preloaderController;
        this.preloaderModel = preloaderModel;
        this.canvasScaler = canvasScaler;
        this.sceneManager = sceneManager;
        this.contextResolver = contextResolver;
        this.applicationModel = applicationModel;
        this.gameInfo = gameInfo;
        this.logger = logger;
    }

    @initializer()
    public async initialization(): Promise<void> {
        try {
            const readyToShowGameScene = new PromiseCompletionSource<void>();
            const resourcesPreloaded = new PromiseCompletionSource<void>();
            const gameSceneResolved = new PromiseCompletionSource<void>();
            const fontResourcePackage = new FontResourcesPackage();
            await fontResourcePackage.load();
            this.view = new HomeScreenPreloaderView(
                this.preloaderResources,
                this.preloaderController,
                this.preloaderModel,
                this.applicationModel,
                this.canvasScaler,
                this.gameInfo,
                resourcesPreloaded,
                gameSceneResolved,
                readyToShowGameScene
            );
            readyToShowGameScene.result.then(() => {
                this.sceneManager.showViewableAsync(this.gameScene);
                this.operationControls.complete();
                this.preloaderResources.destroy();
            });
            resourcesPreloaded.result.then(async () => {
                this.gameScene = await this.contextResolver.resolve(GameScene);
                gameSceneResolved.complete();
            }, (reason: any) => {
                throw reason;
            });
        } catch (error: unknown) {
            this.logger.error(error);
            this.operationControls.complete();
            this.preloaderResources.destroy();
        }
    }
}
